import { useMemo, useCallback } from "react";
import { cn } from "../lib/utils";
import { FilterBar } from "../components/ui/FilterBar";
import { OwnerTag } from "../components/ui/OwnerTag";
import { PriorityDot } from "../components/ui/PriorityDot";
import { EmptyState } from "../components/ui/EmptyState";
import {
  Kanban,
  KanbanBoard,
  KanbanColumn,
  KanbanColumnContent,
  KanbanColumnHandle,
  KanbanItem,
  KanbanItemHandle,
  KanbanOverlay,
} from "../components/reui/kanban";
import type { Card, CardStatus, FilterState } from "../data/types";
import { getTaskMeta } from "../lib/taskMeta";

interface BoardPageProps {
  cards: Card[];
  filters: FilterState;
  onFilterChange: (updates: Partial<FilterState>) => void;
  onResetFilters: () => void;
  onSelectCard: (cardId: string) => void;
  onStatusChange: (cardId: string, status: CardStatus) => void;
}

const columnDefs: Array<{ status: CardStatus; label: string; color: string }> = [
  { status: "planned", label: "Planned", color: "var(--color-text-muted)" },
  { status: "in_progress", label: "In Progress", color: "var(--color-accent)" },
  { status: "blocked", label: "Blocked", color: "var(--color-state-error)" },
  { status: "done", label: "Done", color: "var(--color-state-success)" },
];

export function BoardPage({
  cards,
  filters,
  onFilterChange,
  onResetFilters,
  onSelectCard,
  onStatusChange,
}: BoardPageProps) {
  // Filter to only one_off tasks (and unknown — tasks without explicit type)
  const oneOffCards = useMemo(() => {
    return cards.filter((card) => {
      const meta = getTaskMeta(card);
      return meta.taskType === "one_off" || meta.taskType === "unknown";
    });
  }, [cards]);

  // Build the kanban value: Record<CardStatus, Card[]>
  const kanbanValue = useMemo(() => {
    const map: Record<string, Card[]> = {
      planned: [],
      in_progress: [],
      blocked: [],
      done: [],
    };
    for (const card of oneOffCards) {
      const bucket = map[card.status];
      if (bucket) bucket.push(card);
    }
    return map;
  }, [oneOffCards]);

  // Handle value changes from the kanban (drag and drop)
  const handleValueChange = useCallback(
    (newValue: Record<string, Card[]>) => {
      // Find cards that changed column and fire onStatusChange for each
      for (const [status, items] of Object.entries(newValue)) {
        for (const card of items) {
          if (card.status !== status) {
            onStatusChange(card.id, status as CardStatus);
          }
        }
      }
    },
    [onStatusChange],
  );

  const handleItemClick = useCallback(
    (card: Card) => {
      onSelectCard(card.id);
    },
    [onSelectCard],
  );

  return (
    <div className="h-full flex flex-col">
      {/* Info strip + Filter bar */}
      <div className="shrink-0 px-[var(--spacing-4)] py-[var(--spacing-3)] border-b border-[var(--color-border-stroke)]">
        <div className="flex items-center gap-[var(--spacing-2)] text-[12px] text-[var(--color-text-muted)] mb-[var(--spacing-2)]">
          <span>{oneOffCards.length} one-off tasks</span>
          <span>·</span>
          <span>Use Daily Ops Checklist for recurring tasks</span>
        </div>
        <FilterBar filters={filters} onChange={onFilterChange} onReset={onResetFilters} />
      </div>

      {/* Kanban */}
      <Kanban
        value={kanbanValue}
        onValueChange={handleValueChange}
        getItemValue={(card: Card) => card.id}
        onItemClick={handleItemClick}
        className="flex-1 overflow-hidden"
      >
        <KanbanBoard>
          {columnDefs.map((col) => {
            const colCards = kanbanValue[col.status] ?? [];
            return (
              <KanbanColumn key={col.status} value={col.status}>
                <KanbanColumnHandle>
                  <div className="flex items-center gap-[var(--spacing-2)]">
                    <span
                      className="h-[8px] w-[8px] rounded-full shrink-0"
                      style={{ backgroundColor: col.color }}
                    />
                    <span className="text-[14px] font-semibold text-[var(--color-text-primary)]">
                      {col.label}
                    </span>
                    <span className="text-[14px] text-[var(--color-text-muted)] tabular-nums">
                      {colCards.length}
                    </span>
                  </div>
                </KanbanColumnHandle>

                <KanbanColumnContent value={col.status}>
                  {colCards.length === 0 ? (
                    <EmptyState
                      title="No cards"
                      description="Nothing in this column"
                      variant="board"
                    />
                  ) : (
                    colCards.map((card, idx) => (
                      <KanbanItem key={card.id} value={card.id}>
                        <KanbanItemHandle>
                          <BoardCard
                            card={card}
                            onSelect={onSelectCard}
                            style={{
                              animationDelay: `${Math.min(idx, 9) * 30}ms`,
                            }}
                          />
                        </KanbanItemHandle>
                      </KanbanItem>
                    ))
                  )}
                </KanbanColumnContent>
              </KanbanColumn>
            );
          })}
        </KanbanBoard>

        <KanbanOverlay>
          {(activeItem: unknown) => {
            const card = activeItem as Card | null;
            if (!card) {
              return (
                <div className="rounded-[var(--radius-lg)] bg-[var(--color-bg-surface)] border border-[var(--color-accent)] shadow-lg p-[var(--spacing-3)] opacity-90">
                  <span className="text-[14px] text-[var(--color-text-primary)]">Moving…</span>
                </div>
              );
            }
            return (
              <div className="w-[260px] opacity-90 shadow-lg rounded-[var(--radius-lg)]">
                <BoardCard
                  card={card}
                  onSelect={() => {}}
                />
              </div>
            );
          }}
        </KanbanOverlay>
      </Kanban>
    </div>
  );
}

// ── Board Card ──────────────────────────────────────────────

interface BoardCardProps {
  card: Card;
  onSelect: (id: string) => void;
  style?: React.CSSProperties;
}

function BoardCard({ card, onSelect, style }: BoardCardProps) {
  const dateStr = card.targetDate
    ? new Date(card.targetDate).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
      })
    : null;

  return (
    <div
      onClick={(e) => {
        // Only fire click if not dragging (pointer hasn't moved significantly)
        onSelect(card.id);
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(card.id);
        }
      }}
      className={cn(
        "animate-list-item-in",
        "w-full text-left rounded-[var(--radius-lg)] p-[var(--spacing-3)]",
        "bg-[var(--color-bg-surface)] border border-[var(--color-border-stroke)]",
        "hover:bg-[var(--color-bg-hover)] hover:border-[var(--color-bg-hover)]",
        "transition-all duration-[var(--duration-fast)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg-canvas)]",
        "select-none",
      )}
      style={style}
    >
      {/* Title */}
      <div className="flex items-start gap-[var(--spacing-1_5)]">
        <PriorityDot priority={card.priority} className="mt-[6px] shrink-0" />
        <h4 className="text-[14px] leading-[20px] font-semibold text-[var(--color-text-primary)] line-clamp-2 flex-1">
          {card.title}
        </h4>
      </div>

      {/* Meta */}
      <div className="mt-[var(--spacing-2)] flex items-center gap-[var(--spacing-1)] flex-wrap">
        <OwnerTag owner={card.owner} />
        {card.channelTier && (
          <span className="inline-flex items-center rounded-full bg-[var(--color-bg-hover)] px-[6px] py-[1px] text-[14px] text-[var(--color-text-muted)] ring-1 ring-inset ring-[var(--color-border-stroke)]">
            T{card.channelTier}
          </span>
        )}
        {dateStr && (
          <span className="text-[14px] tabular-nums text-[var(--color-text-muted)] ml-auto">
            {dateStr}
          </span>
        )}
      </div>
    </div>
  );
}
